import { isNonPagePath, withTrailingSlash, type Region } from "./region";

export type StoryblokLink = {
  url?: string;
  cached_url?: string;
  linktype?: "story" | "url" | "email" | "asset";
  target?: string;
};

export function applyRegionPrefix(href: string, region?: Region): string {
  let normalized = href;
  if (normalized.startsWith("/ie/")) normalized = normalized.slice(3);
  else if (normalized === "/ie") normalized = "/";

  if (region !== "uk" || isNonPagePath(normalized)) return normalized;
  if (normalized === "/uk" || normalized.startsWith("/uk/")) return normalized;
  if (normalized === "/") return "/uk";
  return `/uk${normalized}`;
}

export function resolveRichTextHref(rawHref: string, region?: Region): string {
  if (!rawHref || /^(https?:|mailto:|tel:|#)/i.test(rawHref)) return rawHref;
  const [path, hash] = rawHref.split("#");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const resolved = withTrailingSlash(applyRegionPrefix(normalized, region));
  return hash ? `${resolved}#${hash}` : resolved;
}

function isRelativePagePath(raw: string): boolean {
  if (!raw.startsWith("/") || raw.startsWith("//")) return false;
  const path = raw.split(/[?#]/)[0];
  return !/\.[a-z0-9]+$/i.test(path);
}

export function resolveLink(
  link?: StoryblokLink,
  region?: Region,
): { href: string; target?: string } {
  if (!link) return { href: "#" };
  if (link.linktype === "email" && link.url) {
    return { href: `mailto:${link.url}` };
  }
  const raw = link.url || link.cached_url || "";
  if (!raw) return { href: "#" };

  const isInternal =
    link.linktype === "story" ||
    (link.linktype === "url" && isRelativePagePath(raw)) ||
    (!link.linktype && !raw.startsWith("http"));

  if (isInternal) {
    const normalized = raw.startsWith("/") ? raw : `/${raw}`;
    return { href: withTrailingSlash(applyRegionPrefix(normalized, region)) };
  }
  const isExternalUrl = /^https?:\/\//i.test(raw);
  return { href: raw, target: isExternalUrl ? "_blank" : link.target };
}
